import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Chip } from '@cbhq/cds-web/chips';
import { SearchInput } from '@cbhq/cds-web/controls';
import { Box, VStack } from '@cbhq/cds-web/layout';
import { Tooltip } from '@cbhq/cds-web/overlays';
import { TextCaption } from '@cbhq/cds-web/typography/TextCaption';
import { ActionType, AnalyticsEventImportance, ComponentType } from '@cbhq/client-analytics';
import type { CoinbaseProfileMenuResource } from '@cbhq/identity-api-hooks/profile-menu/types';
import { noop } from '@cbhq/identity-shared/utils';

import { NavigationDropdown } from '../components';
import { NavigationDropdownContent } from '../components/NavigationDropdownContent';

import { OrgSwitcherFallback } from './components/OrgSwitcherFallback';
import { ResourceHeader } from './components/ResourceHeader';
import { ResourceItem } from './components/ResourceItem';
import { ResourceMedia } from './components/ResourceMedia';
import { useAnalytics } from './hooks/useAnalytics';
import { useLogEventOnMount } from './hooks/useLogEventOnMount';
import { useResourceSearch } from './hooks/useResourceSearch';
import { HEADER_HEIGHT, MAX_HEIGHT } from './constants';

const messages = defineMessages({
  searchPlaceholder: {
    id: 'OrgSwitcher.searchPlaceholder',
    defaultMessage: 'Search',
    description: 'Placeholder for the search input in the org switcher',
  },
});

export type OrgSwitcherProps = {
  resources: CoinbaseProfileMenuResource[] | undefined;
  activeResource: CoinbaseProfileMenuResource | undefined;
  activeResourceId: string | undefined;
  onResourceSwitch: (resourceId: string) => void;
  /** Pre-formatted count caption, e.g. "Entities (6)". Shown when search is visible. */
  searchCountLabel?: string;
  /** Whether to show the search input when there are many resources. Defaults to true. */
  searchEnabled?: boolean;
};

export function OrgSwitcher({
  resources,
  activeResource,
  activeResourceId,
  onResourceSwitch,
  searchCountLabel,
  searchEnabled = true,
}: OrgSwitcherProps) {
  const { formatMessage } = useIntl();
  const { logEvent } = useAnalytics();

  const { searchQuery, handleSearchChange, searchFilteredResources, shouldShowSearch } =
    useResourceSearch({ resources, activeResourceId });

  const resourceCount = resources?.length ?? 0;

  // Skip only the empty case (component returns null). The fallback loader
  // path (multi resources, no active yet) still counts as a view.
  useLogEventOnMount(
    'org_switcher_view',
    {
      action: ActionType.render,
      componentType: ComponentType.button,
      resource_count: resourceCount,
    },
    AnalyticsEventImportance.high,
    resourceCount > 0,
  );

  const handleResourceItemPress = useCallback(
    (resourceId: string) => {
      const resource = resources?.find((r) => r.identityAccountId === resourceId);
      logEvent('org_switcher_resource_click', {
        action: ActionType.click,
        componentType: ComponentType.button,
        resource_type: resource?.resourceType,
      });
      onResourceSwitch(resourceId);
    },
    [logEvent, onResourceSwitch, resources],
  );

  const resourceList = useMemo(() => {
    return (
      <NavigationDropdownContent maxHeight={`${MAX_HEIGHT - HEADER_HEIGHT}px`}>
        {searchFilteredResources?.map((resource) => {
          return (
            <ResourceItem
              key={resource.identityAccountId}
              resource={resource}
              onPress={handleResourceItemPress}
              selected={resource.identityAccountId === activeResourceId}
            />
          );
        })}
      </NavigationDropdownContent>
    );
  }, [searchFilteredResources, handleResourceItemPress, activeResourceId]);

  const searchInput = useMemo(() => {
    if (!searchEnabled || !shouldShowSearch) {
      return null;
    }
    return (
      <VStack>
        <TextCaption as="span" color="fgMuted" paddingBottom={2} paddingX={3}>
          {searchCountLabel}
        </TextCaption>
        <Box paddingX={2} paddingBottom={2}>
          <SearchInput
            placeholder={formatMessage(messages.searchPlaceholder)}
            value={searchQuery}
            onChangeText={handleSearchChange}
            testID="org-switcher-search"
            compact
          />
        </Box>
      </VStack>
    );
  }, [
    searchEnabled,
    shouldShowSearch,
    searchCountLabel,
    formatMessage,
    searchQuery,
    handleSearchChange,
  ]);

  const content = useMemo(() => {
    return (
      <VStack overflow="hidden">
        <ResourceHeader selectedResource={activeResource} />
        {searchInput}
        {resourceList}
      </VStack>
    );
  }, [activeResource, searchInput, resourceList]);

  if (!resources || resources.length === 0) {
    return null;
  }

  // Single resource → show read-only chip
  // Callers are responsible for filtering out single user-type resources before rendering this component
  if (resources.length === 1) {
    const singleResource = resources[0];
    return (
      <Tooltip content={singleResource.name} placement="bottom">
        <Chip
          start={<ResourceMedia resource={singleResource} small />}
          maxWidth={200}
          height={40}
          onClick={noop}
        >
          {singleResource.name}
        </Chip>
      </Tooltip>
    );
  }

  if (!activeResource) {
    /**
     * Having more than 1 resource but no active resource means we're still defining
     * the active resource.
     */
    return <OrgSwitcherFallback />;
  }

  return (
    <NavigationDropdown
      content={content}
      value={activeResourceId}
      testID="AppSwitcherContent-Dropdown"
      onChange={onResourceSwitch}
    >
      <Tooltip content={activeResource.name} placement="bottom">
        <Chip
          start={<ResourceMedia resource={activeResource} small />}
          maxWidth={200}
          height={40}
          onClick={noop}
        >
          {activeResource.name}
        </Chip>
      </Tooltip>
    </NavigationDropdown>
  );
}
